import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { LISTING_STALE_TIME, useApiQuery } from '@/hooks/api';
import { queryKeys } from '@/shared/lib/queryKeys';

import { resolveListingPageSize } from './listingDataSource';

import type {
  ListingDataSource,
  ListingFetchResult,
  ListingQuerySnapshot,
} from './listingDataSource';
import type { SmartPaginationProps } from './SmartPagination';

// Stable fallback — avoids producing a new array reference on every render when data is absent.
const EMPTY_ROWS: never[] = [];

export type UseFilteredListingQueryOptions<
  TRow,
  TFilters,
  TContext,
  TFilterCounts,
  TResult extends ListingFetchResult<TRow, TFilterCounts> = ListingFetchResult<
    TRow,
    TFilterCounts
  >,
> = {
  dataSource: ListingDataSource<TRow, TFilters, TContext, TFilterCounts>;
  snapshot: ListingQuerySnapshot<TFilters>;
  context: TContext;
  enabled?: boolean;
  /** When false, skips fetch (e.g. until URL state is hydrated). Defaults to true. */
  ready?: boolean;
  pageSize?: number;
  onPageSizeChange?: (pageSize: number) => void;
  onError?: (error: unknown) => void;
  onSuccess?: (result: TResult) => void;
  setPage: (page: number) => void;
};

export function useFilteredListingQuery<
  TRow,
  TFilters,
  TContext = void,
  TFilterCounts = unknown,
  TResult extends ListingFetchResult<TRow, TFilterCounts> = ListingFetchResult<
    TRow,
    TFilterCounts
  >,
>({
  dataSource,
  snapshot,
  context,
  enabled = true,
  ready = true,
  pageSize: requestedPageSize,
  onPageSizeChange,
  onError,
  onSuccess,
  setPage,
}: UseFilteredListingQueryOptions<
  TRow,
  TFilters,
  TContext,
  TFilterCounts,
  TResult
>) {
  const queryClient = useQueryClient();
  const fetchEnabled = enabled && ready;
  const [cursorStack, setCursorStack] = useState<(string | null)[]>([null]);
  const cursorStackRef = useRef<(string | null)[]>([null]);

  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const setPageRef = useRef(setPage);
  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);
  useEffect(() => {
    setPageRef.current = setPage;
  }, [setPage]);

  const snapshotRef = useRef(snapshot);
  useEffect(() => {
    snapshotRef.current = snapshot;
  }, [snapshot]);

  useEffect(() => {
    cursorStackRef.current = cursorStack;
  }, [cursorStack]);

  const listingId = dataSource.listingId;
  const pageSize = requestedPageSize ?? resolveListingPageSize(dataSource);
  const isOffsetPagination = dataSource.paginationMode === 'offset';
  const pageIndex = Math.max(0, snapshot.page - 1);
  const resolvedCursor = isOffsetPagination
    ? null
    : (cursorStack[pageIndex] ?? null);

  // Query dimensions that should reset the page (not readiness or pageSize hydration).
  const dimensionsKey = useMemo(
    () =>
      JSON.stringify({
        q: snapshot.q,
        sort: snapshot.sort,
        filters: snapshot.filters,
        context,
      }),
    [snapshot.q, snapshot.sort, snapshot.filters, context],
  );

  const [prevReset, setPrevReset] = useState({
    dimensionsKey,
    fetchEnabled,
    pageSize,
  });
  if (
    prevReset.dimensionsKey !== dimensionsKey ||
    prevReset.fetchEnabled !== fetchEnabled ||
    prevReset.pageSize !== pageSize
  ) {
    const dimensionsChanged = prevReset.dimensionsKey !== dimensionsKey;
    const wasReady = prevReset.fetchEnabled;
    setPrevReset({ dimensionsKey, fetchEnabled, pageSize });
    if (dimensionsChanged || prevReset.pageSize !== pageSize) {
      setCursorStack([null]);
    }
    // Only reset page between two ready renders when search/filter/sort/context change.
    // Readiness flips (urlReady, enabled) and preference pageSize hydration must not
    // wipe a deep-linked ?page=N.
    if (dimensionsChanged && wasReady && fetchEnabled && snapshot.page !== 1) {
      setPage(1);
    }
  }

  const handlePageSizeChange = useCallback(
    (nextPageSize: number) => {
      onPageSizeChange?.(nextPageSize);
      if (snapshot.page !== 1) {
        setPage(1);
      }
    },
    [onPageSizeChange, setPage, snapshot.page],
  );

  const hasCursorForPage =
    isOffsetPagination ||
    snapshot.page === 1 ||
    Boolean(cursorStack[pageIndex]);

  const queryKey = queryKeys.listings.page(
    listingId,
    {
      q: snapshot.q,
      sort: snapshot.sort,
      page: snapshot.page,
      filters: snapshot.filters,
      cursor: resolvedCursor,
      pageSize,
    },
    context,
  );

  const { data, isLoading, refetch, error } = useApiQuery<TResult>({
    queryKey,
    queryFn: async () => {
      const snap = snapshotRef.current;
      const idx = Math.max(0, snap.page - 1);

      if (
        !isOffsetPagination &&
        snap.page > 1 &&
        !cursorStackRef.current[idx]
      ) {
        setPageRef.current(1);
        throw new Error('Missing cursor for listing page');
      }

      const cursor = isOffsetPagination
        ? null
        : (cursorStackRef.current[idx] ?? null);
      const paginationContext = isOffsetPagination
        ? { page: snap.page, pageSize }
        : { cursor, pageSize };
      const query = dataSource.mapToQueryParams(
        snap,
        context,
        paginationContext,
      );
      return (await dataSource.fetch(query, snap, context)) as TResult;
    },
    enabled: fetchEnabled && hasCursorForPage,
    staleTime: LISTING_STALE_TIME,
    placeholderData: (previous) => previous,
  });

  useEffect(() => {
    if (!data || isOffsetPagination) return;

    setCursorStack((prev) => {
      const snap = snapshotRef.current;
      const next = [...prev];
      while (next.length < snap.page) {
        next.push(null);
      }
      if (data.nextCursor != null) {
        next[snap.page] = data.nextCursor;
      }
      cursorStackRef.current = next;
      return next;
    });
  }, [data, isOffsetPagination]);

  useEffect(() => {
    if (!data) return;
    onSuccessRef.current?.(data);
  }, [data]);

  useEffect(() => {
    if (error && error.message !== 'Missing cursor for listing page') {
      onErrorRef.current?.(error);
    }
  }, [error]);

  const rows = data?.rows ?? EMPTY_ROWS;
  const totalCount = data?.totalCount ?? 0;
  const filterCounts = data?.filterCounts;
  const nextCursor = data?.nextCursor ?? null;
  // Initial load only — keep prior rows visible during background refetch / placeholder.
  const loading = fetchEnabled && hasCursorForPage && isLoading;

  const setRows = useCallback(
    (updater: TRow[] | ((prev: TRow[]) => TRow[])) => {
      queryClient.setQueryData<TResult>(queryKey, (old) => {
        if (!old) return old;
        const nextRows =
          typeof updater === 'function' ? updater(old.rows) : updater;
        return { ...old, rows: nextRows };
      });
    },
    [queryClient, queryKey],
  );

  const reload = useCallback(() => {
    void refetch();
  }, [refetch]);

  const maxReachablePage = useMemo(() => {
    if (isOffsetPagination) {
      const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
      return totalPages;
    }
    if (nextCursor) return Math.max(snapshot.page, cursorStack.length);
    return cursorStack.length;
  }, [
    isOffsetPagination,
    nextCursor,
    snapshot.page,
    cursorStack.length,
    totalCount,
    pageSize,
  ]);

  const handlePageChange = useCallback(
    (page: number) => {
      if (page < 1) return;
      if (isOffsetPagination) {
        const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
        if (page <= totalPages) {
          setPage(page);
        }
        return;
      }
      if (page <= cursorStack.length) {
        setPage(page);
        return;
      }
      if (page === snapshot.page + 1 && nextCursor) {
        setPage(page);
      }
    },
    [
      isOffsetPagination,
      cursorStack.length,
      nextCursor,
      setPage,
      snapshot.page,
      totalCount,
      pageSize,
    ],
  );

  const pagination = useMemo((): SmartPaginationProps | null => {
    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
    const canChangePageSize = onPageSizeChange != null;
    if (totalPages <= 1 && !canChangePageSize) return null;
    return {
      currentPage: snapshot.page,
      totalPages,
      onPageChange: handlePageChange,
      maxReachablePage: isOffsetPagination ? undefined : maxReachablePage,
      showInfo: true,
      totalItems: totalCount,
      itemsPerPage: pageSize,
      showSizeChanger: canChangePageSize,
      onPageSizeChange: handlePageSizeChange,
    };
  }, [
    totalCount,
    pageSize,
    snapshot.page,
    handlePageChange,
    isOffsetPagination,
    maxReachablePage,
    onPageSizeChange,
    handlePageSizeChange,
  ]);

  return {
    loading,
    rows,
    setRows,
    totalCount,
    filterCounts,
    nextCursor,
    reload,
    pagination,
    pageSize,
  };
}
